import axios from 'axios';
import { API_URL } from '../config/api';

/**
 * Layout Service
 * Handles saving and loading user page layouts and templates
 */

const layoutService = {
  /**
   * Get user's saved layout for a specific page
   * @param {string} pageType - 'candidate_profile' or 'client_candidate_profile'
   * @returns {Promise<Object>} Layout object or null if not set
   */
  getUserLayout: async (pageType) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/users/layout/${pageType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.layout;
    } catch (error) {
      console.error('Error fetching user layout:', error);
      return null;
    }
  },

  /**
   * Save user's layout for a specific page
   * @param {string} pageType - 'candidate_profile' or 'client_candidate_profile'
   * @param {Object} layouts - Layout object with breakpointLayouts and hiddenWidgets
   * @returns {Promise<Object>} Saved layout
   */
  saveUserLayout: async (pageType, layouts) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.put(
        `${API_URL}/users/layout/${pageType}`,
        { layouts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving user layout:', error);
      throw error;
    }
  },

  /**
   * Reset user's layout to default for a specific page
   * @param {string} pageType - 'candidate_profile' or 'client_candidate_profile'
   * @returns {Promise<void>}
   */
  resetUserLayout: async (pageType) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_URL}/users/layout/${pageType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error resetting user layout:', error);
      throw error;
    }
  },

  /**
   * Get saved layout templates for a page type
   * @param {string} pageType - 'candidate_profile' or 'client_candidate_profile'
   * @returns {Promise<Array>} Array of templates
   */
  getSavedTemplates: async (pageType) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/users/layout-templates/${pageType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.templates || [];
    } catch (error) {
      console.error('Error fetching templates:', error);
      return [];
    }
  },

  /**
   * Save a layout as a template
   * @param {string} pageType - 'candidate_profile' or 'client_candidate_profile'
   * @param {string} name - Template name
   * @param {Object} data - Layout data to save
   * @returns {Promise<Object>} Saved template
   */
  saveTemplate: async (pageType, name, data) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API_URL}/users/layout-templates`,
        { pageType, name, data },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving template:', error);
      throw error;
    }
  },

  /**
   * Delete a layout template
   * @param {number} templateId - Template ID to delete
   * @returns {Promise<void>}
   */
  deleteTemplate: async (templateId) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${API_URL}/users/layout-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }
};

export default layoutService;
